import { useQuery } from "@tanstack/react-query";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import { ServerInferRequest } from "@ts-rest/core";
import { chartOfAccountsTemplateContract } from "@common/contracts/chart-of-accounts-template.contract";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const client = getBackendAPIClient(chartOfAccountsTemplateContract);
type ActivityQuery = ServerInferRequest<
    typeof chartOfAccountsTemplateContract.listActivityForChartOfAccountsTemplate
>["query"];

export const coaTemplateActivityQueryKeys = {
    all: ["chart-of-accounts-template", "activity"] as const,
    template: (organizationId: string, templateId: string) =>
        [
            ...coaTemplateActivityQueryKeys.all,
            organizationId,
            templateId,
        ] as const,
    list: (organizationId: string, templateId: string, query: ActivityQuery) =>
        [
            ...coaTemplateActivityQueryKeys.template(
                organizationId,
                templateId,
            ),
            query,
        ] as const,
    // Stable, scope-carrying key for the disabled state (missing org/template).
    // Keeps the query cache-partitioned by whatever scope is available instead
    // of colliding every disabled instance onto the same empty key.
    disabled: (
        organizationId: string | null | undefined,
        templateId: string | null | undefined,
    ) =>
        [
            ...coaTemplateActivityQueryKeys.all,
            "disabled",
            organizationId ?? null,
            templateId ?? null,
        ] as const,
};

// Resolves the template Activity list refetch key from the loaded apply-event
// detail's templateId (the History feed no longer carries a flat templateId).
// Returns undefined when the scope, template, or active detail is missing so
// callers safely skip the template-scoped refetch instead of guessing a key.
export function buildTemplateActivityQueryKey({
    organizationId,
    templateId,
}: {
    organizationId: string | null | undefined;
    templateId: string | null | undefined;
}): readonly string[] | undefined {
    if (!organizationId || !templateId) {
        return undefined;
    }
    return coaTemplateActivityQueryKeys.template(organizationId, templateId);
}

export function useChartOfAccountsTemplateActivity({
    templateId,
    query,
    enabled = true,
}: {
    templateId: string | null;
    query: ActivityQuery;
    enabled?: boolean;
}) {
    const { organizationId } = useScope();

    const queryKey =
        organizationId && templateId
            ? coaTemplateActivityQueryKeys.list(
                  organizationId,
                  templateId,
                  query,
              )
            : coaTemplateActivityQueryKeys.disabled(organizationId, templateId);

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!organizationId || !templateId) {
                throw new Error("Missing template Activity context");
            }
            const response =
                await client.listActivityForChartOfAccountsTemplate({
                    params: {
                        organizationId,
                        chartOfAccountsTemplateId: templateId,
                    },
                    query,
                });
            return response.body;
        },
        enabled: !!organizationId && !!templateId && enabled,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}
